import { useEffect, useRef } from 'react';

interface AnnouncerProps {
  message: string;
  priority?: 'polite' | 'assertive';
  clearOnUnmount?: boolean;
}

export const ScreenReaderAnnouncer = ({ 
  message, 
  priority = 'polite', 
  clearOnUnmount = true 
}: AnnouncerProps) => {
  const announcerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (announcerRef.current && message) {
      // Clear previous message and force re-announcement
      announcerRef.current.textContent = '';
      setTimeout(() => {
        if (announcerRef.current) {
          announcerRef.current.textContent = message;
        }
      }, 100);
    }
  }, [message]);

  useEffect(() => {
    return () => {
      if (clearOnUnmount && announcerRef.current) {
        announcerRef.current.textContent = '';
      }
    };
  }, [clearOnUnmount]);

  return (
    <div
      ref={announcerRef}
      aria-live={priority}
      aria-atomic="true"
      className="sr-only"
    />
  );
};

// Global announcer hook
let globalAnnouncerInstance: ((message: string, priority?: 'polite' | 'assertive') => void) | null = null;

export const useScreenReader = () => {
  const announce = (message: string, priority: 'polite' | 'assertive' = 'polite') => {
    if (globalAnnouncerInstance) {
      globalAnnouncerInstance(message, priority);
    } else {
      // Fallback: create temporary announcer
      const announcer = document.createElement('div');
      announcer.setAttribute('aria-live', priority);
      announcer.setAttribute('aria-atomic', 'true');
      announcer.className = 'sr-only';
      document.body.appendChild(announcer);
      
      setTimeout(() => {
        announcer.textContent = message;
        setTimeout(() => {
          document.body.removeChild(announcer);
        }, 1000);
      }, 100);
    }
  };

  return { announce };
};

// Global announcer component
export const GlobalScreenReaderAnnouncer = () => {
  const announcerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    globalAnnouncerInstance = (message: string, priority: 'polite' | 'assertive' = 'polite') => {
      if (announcerRef.current) {
        announcerRef.current.setAttribute('aria-live', priority);
        announcerRef.current.textContent = '';
        setTimeout(() => {
          if (announcerRef.current) {
            announcerRef.current.textContent = message;
          }
        }, 100);
      }
    };

    return () => {
      globalAnnouncerInstance = null;
    };
  }, []);

  return (
    <div
      ref={announcerRef}
      aria-live="polite"
      aria-atomic="true"
      className="sr-only"
    />
  );
};